// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import Link from "@insite/mobius/Link"; // Starter Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
// import InjectableCss from "@insite/mobius/utilities/InjectableCss"; // Starter Custom Code
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import TwoButtonModal from "@store/Overrides/Components/TwoButtonModal";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import { LinkModelWithModal } from "@store/Framework/Store/Store/Links/LinksSelectors";
import { LINK_MODAL_DEFAULT_VALUES, linkModalFields } from "@store/Library/Components/LinkList/fields/linkModal";
import { useLinkModal } from "@store/Framework/Common/Hooks/useLinkModal";
/* Store Custom Code - End */

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
    // Starter Custom Code
    showSeparator = "showSeparator",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModelWithModal[]; // Store Custom Code
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.showSeparator]: boolean;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    // Starter Custom Code
    horizontalList?: StyledProp;
    horizontalItem?: StyledProp;
    verticalList?: StyledProp;
    verticalItem?: StyledProp;
    titleLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    titleWrapper: {
        css: spacingStyles(),
    },
    title: {
        forwardAs: "p",
    },
    horizontalItem: css`
        ${inlineSeparatorStyles({ spacing: "1.4em" })}
        display: inline-block;
    `,
    verticalItem: css`
        ${spacingStyles(null, "0.5em")}
    `,
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    const { isModalOpen, pendingTitle, openModal, confirmModal, closeModal } = useLinkModal(); // Store Custom Code
    /* Starter Custom Code - Start */
    const listDirectionStyles = css`
        ${fields.direction === "horizontal" ? styles.horizontalList : styles.verticalList}
    `;
    /* Starter Custom Code - End */
    if (hasMissingCategories) {
        return null;
    }

    const alignmentStyles = {
        css: css`
            ${cssProp("text-align", fields.alignment) /* Starter Custom Code */}
            ${styles.linkListWrapper?.css}
        `,
    };

    const directionStyles = {
        css: css`
            /* Starter Custom Code - Start */
            ${fields.direction === "horizontal" ? styles.horizontalItem : styles.verticalItem}
            ${!fields.showSeparator ? `&::after { display: none }` : null}
            /* Starter Custom Code - End */
            ${styles.linkWrapper?.css}
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                {/* Starter Custom Code - Start */}
                {fields.title || (titleLink && titleLink.title) ? (
                    <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                        {titleLink?.url ? (
                            <Link className="list-title" href={titleLink.url} {...styles.titleLink}>
                                {fields.title || titleLink.title}
                            </Link>
                        ) : (
                            <Typography className="list-title" {...styles.title}>
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                ) : null}
                {links?.length ? (
                    <StyledWrapper as="ul" css={listDirectionStyles}>
                        {/* Store Custom Code - Start */}
                        {links.map(({ title, url }, index) => {
                            if (!url) return null;

                            const {
                                openInNewWindow,
                                overriddenTitle,
                                showSwitchModal,
                                modalHeadline: fieldModalHeadline,
                                modalBody: fieldModalBody,
                                modalCancelLabel: fieldModalCancelLabel,
                                modalCtaLabel: fieldModalCtaLabel,
                            } = fields.links[index].fields;
                            const _title: string = overriddenTitle || title || "";
                            const target: "_self" | "_blank" = openInNewWindow ? "_blank" : "_self";
                            const key = `${url}--${index}`;

                            const modalHeadline =
                                fieldModalHeadline && isNonEmptyString(fieldModalHeadline)
                                    ? fieldModalHeadline
                                    : LINK_MODAL_DEFAULT_VALUES.modalHeadline;
                            const modalBody =
                                fieldModalBody && isNonEmptyString(fieldModalBody)
                                    ? fieldModalBody
                                    : LINK_MODAL_DEFAULT_VALUES.modalBody;
                            const modalCancelLabel =
                                fieldModalCancelLabel && isNonEmptyString(fieldModalCancelLabel)
                                    ? fieldModalCancelLabel
                                    : LINK_MODAL_DEFAULT_VALUES.modalCancelLabel;
                            const modalCtaLabel =
                                fieldModalCtaLabel && isNonEmptyString(fieldModalCtaLabel)
                                    ? fieldModalCtaLabel
                                    : LINK_MODAL_DEFAULT_VALUES.modalCtaLabel;

                            return (
                                <StyledWrapper as="li" className="links-wrapper" key={index} {...directionStyles}>
                                    <Link
                                        {...styles.link}
                                        className="list-link"
                                        href={showSwitchModal ? undefined : url}
                                        key={`${url}.${_title}`}
                                        target={showSwitchModal ? undefined : target}
                                        onClick={(e: any) => {
                                            if (!showSwitchModal) return;
                                            e.preventDefault();
                                            openModal({ key, href: url, title: _title, target });
                                        }}
                                        onKeyDown={(e: any) => {
                                            if (!showSwitchModal) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                openModal({ key, href: url, title: _title, target });
                                            }
                                        }}
                                    >
                                        {_title}
                                    </Link>
                                    {showSwitchModal && (
                                        <TwoButtonModal
                                            {...styles.switchSiteModal}
                                            modalIsOpen={isModalOpen[key] && isModalOpen[key] === true}
                                            headlineText={modalHeadline.replace("{0}", pendingTitle)}
                                            messageText={modalBody.replace("{0}", pendingTitle)}
                                            cancelButtonText={modalCancelLabel}
                                            submitButtonText={modalCtaLabel}
                                            onCancel={() => closeModal(key)}
                                            onSubmit={() => confirmModal(key)}
                                        />
                                    )}
                                </StyledWrapper>
                            );
                        })}
                        {/* Store Custom Code - End */}
                    </StyledWrapper>
                ) : null}
                {/* Starter Custom Code - End */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            /* Starter Custom Code - Start */
            {
                name: fields.showSeparator,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: x => x.fields.direction === "horizontal",
                tab: basicTab,
            },
            /* Starter Custom Code - End */
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                    /* Starter Custom Code - Start */
                    {
                        displayName: "Unset",
                        value: "",
                    },
                    /* Starter Custom Code - End */
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    ...linkModalFields /* Store Custom Code */,
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
